import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';
import { ArrowLeft, Check } from 'lucide-react';
import { BRAND } from '@/data/site';
import { linkify } from '@/lib/linkify';

const LAST_UPDATED = 'May 8, 2026';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    blocks: [
      {
        type: 'p',
        text:
          'By submitting a quote request, scheduling a service, or engaging Ohana Property Services in any capacity, you agree to these Terms of Service. If you do not agree, do not submit a request or engage our services.',
      },
      {
        type: 'p',
        text:
          'These Terms of Service govern all services provided by Ohana Property & Transition Services LLC, operating as Ohana Property Services, a registered Indiana limited liability company.',
      },
    ],
  },
  {
    title: '2. Scope of Services',
    blocks: [
      {
        type: 'p',
        text:
          'Ohana Property Services provides property cleanout, junk removal, estate cleanout, debris removal, appliance removal, furniture removal, yard waste removal, and donation-conscious item recovery services across Central Indiana.',
      },
      { type: 'p', text: 'Ohana accepts the following standard nonhazardous materials:' },
      {
        type: 'list',
        items: [
          'Furniture and household items',
          'General household junk and clutter',
          'Move-out and rental turnover debris',
          'Estate cleanout contents',
          'Appliances that do not contain CFCs',
          'Yard waste and outdoor items',
          'Mattresses and bedding',
          'Clothing and soft goods',
          'Non-regulated construction debris',
          'Donation-eligible household goods with written authorization',
        ],
      },
      {
        type: 'p',
        text:
          'Carpet removal is available as an add-on service for property cleanouts and estate cleanouts only. Ask about pricing when requesting your quote.',
      },
    ],
  },
  {
    title: '3. Items We Do Not Accept',
    blocks: [
      {
        type: 'p',
        text:
          'Ohana Property Services does not accept, transport, handle, remediate, or dispose of the following materials:',
      },
      {
        type: 'list',
        items: [
          'Hazardous waste of any kind',
          'Medical waste, needles, or sharps',
          'Chemicals, solvents, or pesticides',
          'Motor oil or oil-based products',
          'Wet paint or oil-based paint',
          'Unknown liquids or sealed containers',
          'Lead acid batteries',
          'Propane tanks or pressurized cylinders',
          'Asbestos-containing materials',
          'Lead-contaminated materials',
          'Mold remediation materials',
          'Human or animal waste or biohazards',
          'Dead animals',
          'Ammunition, firearms, or explosives',
          'Electronics requiring certified e-waste recycling (unless pre-arranged and priced separately)',
          'CFC-containing appliances (unless pre-arranged)',
          'Whole tires (unless pre-arranged)',
          'Solar panels',
          'Any regulated material requiring special licensing, manifesting, or disposal permits',
        ],
      },
      {
        type: 'p',
        text:
          'These restrictions are based on Bartholomew County Landfill rules, City of Columbus solid waste guidelines, and Indiana solid waste regulations. Violations of these rules carry significant legal and financial penalties.',
      },
      { type: 'p', text: 'For proper disposal of prohibited items in Central Indiana:' },
      {
        type: 'list',
        items: [
          'BCSWMD: bcswmd.com | (812) 376-2614',
          'Columbus Dept. of Public Works: columbus.in.gov/public-works | (812) 376-2509',
        ],
      },
    ],
  },
  {
    title: '4. Pricing and Estimates',
    blocks: [
      {
        type: 'p',
        text:
          'All quotes provided by Ohana Property Services are estimates based on photos, property description, and information provided by the customer prior to the job.',
      },
      { type: 'p', text: 'Final pricing may change based on:' },
      {
        type: 'list',
        items: [
          'Actual volume and weight of items',
          'Labor requirements and access conditions',
          'Stairs, long carry distances, or difficult access',
          'Disposal facility fees and surcharges',
          'Recycling fees for special items',
          'Special item handling fees',
          'Materials discovered on site not included in the original scope',
          'Facility rejection of undisclosed items',
          'Hazardous material discovery requiring stop work',
          'Return trips or additional visits required',
          'Third-party provider costs for regulated materials',
        ],
      },
      {
        type: 'p',
        text:
          'Customer will be notified of any pricing changes before additional work proceeds where reasonably possible.',
      },
    ],
  },
  {
    title: '5. Job Scope and Multiple Visits',
    blocks: [
      {
        type: 'p',
        text:
          'Ohana Property Services arrives prepared based on photos and information provided before the job. Most standard junk removal jobs are completed efficiently in one visit.',
      },
      {
        type: 'p',
        text:
          'Larger jobs, including full home cleanouts, estate cleanouts, heavy debris, and high-volume properties, may require more than one visit. Ohana will assess the full scope from photos before scheduling and provide an honest timeline so customers know what to expect before we arrive.',
      },
      {
        type: 'p',
        text:
          'Ohana does not guarantee completion of any job in a single visit where volume, weight, or access conditions make that impractical or unsafe.',
      },
    ],
  },
  {
    title: '6. Customer Responsibilities',
    blocks: [
      { type: 'p', text: 'By requesting service you confirm:' },
      {
        type: 'list',
        items: [
          'You are the property owner or have legal authority to authorize removal of items from the property',
          'You have disclosed all known hazardous, restricted, or special-handling items in your quote request',
          'You understand that undisclosed items may result in additional fees, stop work, or inability to complete the job',
          'You authorize Ohana to access the property as agreed to perform the requested services',
          'You have read and understood the items Ohana does not accept as listed in Section 3',
        ],
      },
    ],
  },
  {
    title: '7. Stop Work Policy',
    blocks: [
      { type: 'p', text: 'Ohana reserves the right to stop work immediately upon discovery of:' },
      {
        type: 'list',
        items: [
          'Hazardous or unknown materials',
          'Needles, sharps, or medical waste',
          'Chemicals, fuel, or pesticides',
          'Suspected asbestos, lead, mold, or biohazards',
          'Unclear customer authority or ownership disputes',
          'Disputed items',
          'Safety hazards to crew',
          'Any condition that prevents safe or legal disposal',
        ],
      },
      {
        type: 'p',
        text:
          'When stop work occurs Ohana will notify the customer, document the issue, and provide options including:',
      },
      {
        type: 'list',
        items: [
          'Continuing with nonhazardous items if area is safe',
          'Pausing the job pending third-party remediation',
          'Canceling affected scope with adjustment to pricing',
        ],
      },
      {
        type: 'p',
        text:
          'Customer remains responsible for costs incurred up to the point of stop work for items already removed or disposed of.',
      },
    ],
  },
  {
    title: '8. Facility Rejection',
    blocks: [
      {
        type: 'p',
        text:
          'If a disposal facility, recycling center, or donation partner rejects an item due to its nature or facility rules, customer remains responsible for any added disposal fees, handling costs, return trips, or third-party provider costs required to properly dispose of that item, particularly if the item was not disclosed at the time of quoting.',
      },
      { type: 'p', text: 'Ohana will never illegally dump rejected materials.' },
    ],
  },
  {
    title: '9. Donation and Reuse',
    blocks: [
      {
        type: 'p',
        text:
          'Donation or reuse of items is only permitted with written customer authorization confirmed at the time of service.',
      },
      { type: 'p', text: 'Ohana does not:' },
      {
        type: 'list',
        items: [
          'Assign monetary value to donated items',
          'Issue tax receipts (unless a qualified nonprofit partner independently provides one)',
          'Guarantee specific donation destinations',
        ],
      },
      {
        type: 'p',
        text:
          'Through The Hearth Project initiative, Ohana makes every reasonable effort to redirect usable items to families and nonprofits in Central Indiana rather than disposing of them.',
      },
    ],
  },
  {
    title: '10. Limitation of Liability',
    blocks: [
      { type: 'p', text: 'Ohana Property Services is not liable for:' },
      {
        type: 'list',
        items: [
          'Pre-existing property damage at the service address',
          'Damage to items not included in the agreed scope',
          'Costs arising from undisclosed hazardous or prohibited materials discovered after job commencement',
          'Facility rejection fees for undisclosed items',
          'Third-party costs for regulated waste handling',
          'Delays caused by customer failure to disclose material information prior to scheduling',
        ],
      },
      {
        type: 'p',
        text:
          "Ohana's total liability for any claim arising from services provided shall not exceed the amount paid by the customer for the specific job in dispute.",
      },
    ],
  },
  {
    title: '11. Compliance with Local and State Regulations',
    blocks: [
      {
        type: 'p',
        text: 'All Ohana Property Services operations comply with applicable requirements including:',
      },
      {
        type: 'list',
        items: [
          'Bartholomew County Solid Waste Management District rules and facility guidelines',
          'City of Columbus Department of Public Works solid waste and sanitation guidelines',
          'Indiana solid waste disposal regulations',
          'Indiana Department of Environmental Management (IDEM) requirements as applicable',
          'Indiana Department of Transportation (INDOT) commercial vehicle load securement and weight regulations',
          'Federal EPA solid waste management baseline regulations',
          'Applicable OSHA general industry safety standards for field operations',
          'Indiana Bureau of Motor Vehicles commercial vehicle requirements as applicable',
          'All applicable local ordinances in Central Indiana service areas',
          'Donation partner acceptance policies for items redirected through The Hearth Project',
        ],
      },
      { type: 'p', text: 'For local disposal guidelines and resources:' },
      {
        type: 'list',
        items: [
          'BCSWMD: bcswmd.com | (812) 376-2614',
          'Columbus Dept. of Public Works: columbus.in.gov/public-works | (812) 376-2509',
          'Indiana IDEM: in.gov/idem',
          'INDOT: in.gov/indot',
          'Federal EPA: epa.gov/smm (Sustainable Materials Management)',
        ],
      },
    ],
  },
  {
    title: '12. Subcontractors and Vendors',
    blocks: [
      {
        type: 'p',
        text:
          "Ohana may use subcontractors or vendors to assist with service delivery. All subcontractors are required to follow Ohana's Disposal SOP and applicable regulations.",
      },
      {
        type: 'p',
        text:
          'Subcontractors may not handle excluded or regulated materials unless properly licensed, insured, certified, and approved in writing by Ohana management.',
      },
    ],
  },
  {
    title: '13. Changes to Terms of Service',
    blocks: [
      {
        type: 'p',
        text:
          'Ohana Property Services reserves the right to update these Terms of Service at any time. The date at the top of this page indicates when it was last updated. Continued use of our services after any update constitutes acceptance of the revised terms.',
      },
    ],
  },
  {
    title: '14. Governing Law',
    blocks: [
      {
        type: 'p',
        text:
          'These Terms of Service are governed by the laws of the State of Indiana. Any disputes arising from services provided by Ohana Property Services shall be subject to the jurisdiction of the courts of Bartholomew County, Indiana.',
      },
    ],
  },
  {
    title: '15. Contact Us',
    blocks: [
      { type: 'p', text: 'For questions about these Terms of Service contact:' },
      {
        type: 'list',
        items: [
          'Ohana Property Services',
          'Ohana Property & Transition Services LLC',
          'Email: ohanas@ohanapropertyexperts.com',
          'Phone: (812) 302-6833',
          'Website: ohanapropertyexperts.com',
        ],
      },
    ],
  },
];

function Block({ block }) {
  if (block.type === 'list') {
    return (
      <ul className="my-5 space-y-2.5">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-1.5 grid h-4 w-4 flex-none place-items-center rounded-full bg-ohana-accent/15 text-ohana-accent">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span className="text-base md:text-lg leading-relaxed text-ohana-text">
              {linkify(it)}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="text-base md:text-lg leading-relaxed text-ohana-text mb-4">
      {linkify(block.text)}
    </p>
  );
}

export default function Terms() {
  return (
    <>
      <SeoHead
        title="Terms of Service | Ohana Property Services"
        description="Terms of Service for Ohana Property Services covering scope, acceptance policy, prohibited items, pricing, stop-work policy, donation authorization, liability, governing law, and more."
        canonical="https://ohanapropertyexperts.com/terms"
      />

      <section className="bg-ohana-primary text-ohana-bg">
        <div className="container-ohana pt-24 pb-16 md:pt-32 md:pb-20 max-w-4xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-ohana-bg/70 hover:text-white transition mb-8"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back Home
          </Link>
          <p className="overline">Legal</p>
          <h1
            className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05]"
            data-testid="terms-title"
          >
            Terms of Service
          </h1>
          <p className="mt-5 text-base text-ohana-bg/80 max-w-2xl leading-relaxed">
            Ohana Property Services is a trade name of Ohana Property &amp; Transition Services LLC.
          </p>
          <p className="mt-3 text-sm text-ohana-bg/70">Last updated {LAST_UPDATED}</p>
        </div>
      </section>

      <section className="bg-ohana-bg" data-testid="terms-body">
        <div className="container-ohana py-16 md:py-24">
          <div className="mx-auto max-w-3xl">
            <div className="space-y-12">
              {SECTIONS.map((s, i) => (
                <div key={i} data-testid={`terms-section-${i + 1}`}>
                  <h2 className="font-serif text-2xl md:text-3xl text-ohana-primary leading-snug mb-5">
                    {s.title}
                  </h2>
                  {s.blocks.map((b, bi) => (
                    <Block key={bi} block={b} />
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-16 rounded-2xl border border-ohana-border bg-ohana-surface p-6 md:p-8">
              <h3 className="font-serif text-xl text-ohana-primary">Need a copy or have questions?</h3>
              <p className="mt-3 text-ohana-text leading-relaxed">
                Email{' '}
                <a
                  href={`mailto:${BRAND.email}`}
                  className="font-semibold text-ohana-primary underline decoration-ohana-accent decoration-2 underline-offset-4"
                  data-testid="terms-email-link"
                >
                  {BRAND.email}
                </a>{' '}
                or call{' '}
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  className="font-semibold text-ohana-primary underline decoration-ohana-accent decoration-2 underline-offset-4"
                  data-testid="terms-phone-link"
                >
                  {BRAND.phone}
                </a>
                .
              </p>
              <p className="mt-5 text-sm text-ohana-text-muted">
                See also our{' '}
                <Link
                  to="/privacy"
                  className="font-semibold text-ohana-primary underline decoration-ohana-accent decoration-2 underline-offset-4"
                  data-testid="terms-to-privacy-link"
                >
                  Privacy Policy
                </Link>
                .
              </p>
              <p className="mt-6 pt-5 border-t border-ohana-border text-xs text-ohana-text-muted leading-relaxed">
                {BRAND.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
